import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import Spinner from '../UI/Spinner';

const PendingReviewScreen = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { direction } = useRTL();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-md flex items-center justify-center p-4 z-50" dir={direction}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="relative max-w-2xl w-full mx-auto"
      >
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent-400/10 to-transparent pointer-events-none" />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-24 h-24 mx-auto mb-6 bg-blue-500 text-white rounded-full flex items-center justify-center"
          >
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </motion.div>

          <div className="text-center relative z-10">
            <h2 className="heading-mono text-2xl sm:text-3xl mb-4 text-gray-800 dark:text-gray-100">
              {t('onboarding.pendingReview.title')}
            </h2>

            <p className="text-mono text-base mb-6 text-gray-700 dark:text-gray-300">
              {t('onboarding.pendingReview.message')}, {user?.firstName || user?.email}!
            </p>

            <div className="rounded-xl p-6 mb-8 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-4">
                <Spinner size="lg" className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
                <div className="text-left flex-1">
                  <h3 className="text-mono font-semibold mb-2 text-blue-700 dark:text-blue-200">
                    {t('onboarding.pendingReview.whatsHappening') || "What's happening now"}:
                  </h3>
                  <ul className="text-mono text-sm space-y-2 text-blue-600 dark:text-blue-300">
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>{t('onboarding.pendingReview.step1') || 'Our team is reviewing and optimizing your search queries'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>{t('onboarding.pendingReview.step2') || 'Running initial competitor analysis across AI engines'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent-400 mt-0.5">✓</span>
                      <span>{t('onboarding.pendingReview.step3') || 'Preparing your personalized dashboard'}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-lg p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-mono text-sm text-yellow-700 dark:text-yellow-200">
                ⏱️ {t('onboarding.pendingReview.estimatedTime') || 'Estimated time: 2-3 hours'}
              </p>
              <p className="text-mono text-xs mt-1 text-yellow-600 dark:text-yellow-300">
                {t('onboarding.pendingReview.emailNotification') || "We'll send you an email as soon as your dashboard is ready"}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-mono font-semibold mb-3 text-gray-800 dark:text-gray-100">
                {t('onboarding.pendingReview.whatYouGet') || "What you'll get"}:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="text-accent-400 text-2xl mb-2">🎯</div>
                  <p className="text-mono text-xs text-gray-700 dark:text-gray-200">
                    {t('onboarding.pendingReview.benefit1') || '25 optimized search queries'}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="text-accent-400 text-2xl mb-2">🤖</div>
                  <p className="text-mono text-xs text-gray-700 dark:text-gray-200">
                    {t('onboarding.pendingReview.benefit2') || 'Analysis across 4 AI engines'}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="text-accent-400 text-2xl mb-2">📊</div>
                  <p className="text-mono text-xs text-gray-700 dark:text-gray-200">
                    {t('onboarding.pendingReview.benefit3') || 'Competitor visibility rankings'}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-mono text-sm text-gray-600 dark:text-gray-400">
              <p>{t('onboarding.pendingReview.contactUs') || 'Questions? Contact us at'}</p>
              <a
                href="mailto:support@eyehill.com"
                className="text-accent-400 hover:text-accent-300 transition-colors"
              >
                support@eyehill.com
              </a>
            </div>

            <div className="mt-8">
              <button
                onClick={logout}
                className="text-mono text-sm underline transition-colors text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('common.buttons.logout')}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PendingReviewScreen;
